import type { MediaType } from '../fields/media-type-config';

export interface MediaMetadata {
  created_at?: string | null;
  created?: string | null;
  uploaded_at?: string | null;
  exif?: {
    date_time_original?: string | null;
    date_time?: string | null;
  } | null;
  metadata?: {
    format?: {
      tags?: {
        creation_time?: string | null;
      } | null;
    } | null;
  } | null;
}

function isValidDate(date: Date): boolean {
  return Number.isFinite(date.getTime());
}

/**
 * Formats a Date as `YYYY-MM-DD HH:MM:SS` (local time, zero-padded).
 */
function formatAsYMDHMS(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${d} ${h}:${mi}:${s}`;
}

/**
 * Formats a photo EXIF timestamp to match the media viewer's "Date:" field.
 *
 * The EXIF timestamp is in *local* time, despite being returned in ISO-like
 * format (e.g. "2025-05-07T13:14:15.000Z"). The media viewer strips the
 * timezone to prevent conversion, so we do the same: "YYYY-MM-DD HH:MM:SS".
 */
function formatPhotoDate(metadata: MediaMetadata): string | null {
  const timestamp = metadata.exif?.date_time_original ?? metadata.exif?.date_time;

  if (typeof timestamp !== 'string' || timestamp === '') return null;

  const tIndex = timestamp.indexOf('T');
  if (tIndex <= 0) return null;

  const date = timestamp.slice(0, tIndex);
  const fullTime = timestamp.slice(tIndex + 1);
  if (!fullTime) return null;

  // Strip milliseconds and timezone suffix
  const time = fullTime.split('.')[0].replace(/[Zz]$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
  if (!time) return null;

  return `${date} ${time}`;
}

/**
 * Formats a video/audio creation_time to match the media viewer's "Date:" field.
 *
 * The media viewer parses with `new Date(creation_time)` and falls back to
 * space-separated "YYYY-MM-DD HH:mm:ss" → `new Date("...T...Z")`. Both paths
 * end with `toLocaleString()`.
 */
function formatTrackDate(metadata: MediaMetadata): string | null {
  const creationTime = metadata.metadata?.format?.tags?.creation_time;

  if (typeof creationTime !== 'string' || creationTime === '') return null;

  const parsedDateTime = new Date(creationTime);

  if (isValidDate(parsedDateTime)) {
    return formatAsYMDHMS(parsedDateTime);
  }

  // Fallback: space-separated format, same as track-metadata-view.tsx
  const [date, fullTime] = creationTime.split(' ');
  if (!date || !fullTime) return null;

  const fallbackDateTime = new Date(`${date}T${fullTime}Z`);

  if (!isValidDate(fallbackDateTime)) return null;

  return formatAsYMDHMS(fallbackDateTime);
}

/**
 * Formats a created_at/created API timestamp for media types without
 * type-specific date fields (sketch, attachment, signature).
 */
function formatCreatedTimestamp(metadata: MediaMetadata): string | null {
  const timestamp = metadata.created_at ?? metadata.created ?? metadata.uploaded_at;

  if (typeof timestamp !== 'string' || timestamp === '') return null;

  const date = new Date(timestamp);

  if (!isValidDate(date)) return null;

  return formatAsYMDHMS(date);
}

/**
 * Extracts the best available raw date string for a media item's metadata,
 * matching the format shown in the media viewer's "Date:" field.
 *
 * Returns null when no valid date can be extracted.
 */
export function extractMediaDate(metadata: MediaMetadata, mediaType: MediaType | undefined): string | null {
  if (mediaType === 'photo') {
    return formatPhotoDate(metadata) ?? formatCreatedTimestamp(metadata);
  }

  if (mediaType === 'video' || mediaType === 'audio') {
    return formatTrackDate(metadata) ?? formatCreatedTimestamp(metadata);
  }

  return formatCreatedTimestamp(metadata);
}

/**
 * Formats a raw date string (from extractMediaDate) as a display title
 * prefixed with "Date: ". Returns "Date: Unavailable" when raw is null
 * (metadata was fetched but no date found). Returns empty string when raw
 * is undefined (metadata not yet fetched) to avoid flashing the fallback.
 */
export function formatMediaDateTitle(raw: string | null | undefined): string {
  if (raw === undefined) return '';
  return raw ? `Date: ${raw}` : 'Date: Unavailable';
}
